import { useLocation } from "react-router-dom";
import { Box } from "@wish-ui";
import styled from "@emotion/styled";
import { PAGE_URLS } from "utils/constants";
import {
  SCREEN_MS,
  SCREEN_EASE,
  HOME_PUSH_PERCENT,
} from "components/SetupJourneyWidget/entryPointMotion";
import {
  useIsSetupJourneyOverlayMounted,
  useIsSetupJourneyOverlayOpen,
} from "./useSetupJourneyOverlay";

/*
 * ---------------------------------------------------------------------------
 * CAT-407 — the other half of the screen motion: Home getting pushed.
 * ---------------------------------------------------------------------------
 *
 * Wraps the routed page so it can travel 0 -> -22% while the journey travels
 * 100% -> 0. Both are 460ms on the same curve; the *difference in distance* is
 * what reads as depth. Matched travel would read as one sheet of paper.
 *
 * ## The transform trap
 *
 * `transform` and `will-change: transform` each make an element a containing
 * block for its `position: fixed` descendants. Declared here at rest, every
 * fixed thing inside the routed page would silently re-anchor to this element —
 * and this element is as tall as the whole scrolling page, so a `bottom: 0`
 * would resolve to the bottom of the *document*, not the viewport. The footer /
 * bottom nav, the FTUX home takeover and the product-tour spotlight would all
 * leave the screen.
 *
 * So neither property exists at rest. Both are scoped to `[data-pushed="true"]`,
 * which is only present while the journey is actually covering Home and the
 * things it would re-anchor are behind it anyway. The entry point's own dock is
 * `position: sticky`, which resolves against the scrollport and is immune
 * either way.
 *
 * ## Isolation
 *
 * `isolation: isolate` is a second, separate job and is scoped to
 * `[data-overlay="true"]` — present whenever the journey is on screen, on every
 * surface, not just Home. It makes the page one stacking context, so the
 * overlay's z-index of 41 clears Home's header (200), the entry point's dock
 * (150) and the footer (1000) without having to outrank the body-portaled
 * layers the journey itself depends on (bottom sheets at 1001, drawers at 1200,
 * toasts, the force-update wall). `isolation` creates a stacking context and
 * nothing else — unlike `transform` it is not a containing block for anything,
 * so it is safe to switch on without the push.
 *
 * Both are scoped rather than permanent: a stacking context here at rest would
 * re-rank every existing overlay in the app against the ones mounted outside
 * the router.
 */
/*
 * The clipper for Home's travel.
 *
 * The app is a centred column, not the full window, so a pushed Home has
 * somewhere to slide *to*: at -22% it leaves the column and sits over the page
 * gutter, with the journey's leading-edge shadow falling across it. On a phone
 * the column is the viewport and this never shows, which is exactly why it
 * survives QA on a device and fails on a desktop.
 *
 * `overflow-x: clip` rather than `hidden`: `hidden` computes the other axis to
 * `auto` and makes this a scroll container, which would re-anchor HomeHeader's
 * `position: sticky` to it instead of the viewport and stop the header sticking.
 * `clip` clips without creating one. Pre-Safari-16 ignores it and simply keeps
 * today's behaviour, which is only ever visible on a window wider than the
 * column.
 *
 * ## Why both carry `width: inherit`
 *
 * These two divs sit between `.App` (a fixed 28rem column, `App.css:13`) and
 * every routed page, and ~20 places in the app size themselves with
 * `width: inherit` — most of them `position: fixed` bottom CTA bars, which have
 * no other width to resolve against. `inherit` copies the parent's *computed*
 * value, and a plain `<div>` computes `width: auto`, so a wrapper that does not
 * forward the column's width silently turns every one of those into a
 * shrink-to-fit box. Forwarding it here keeps the chain intact.
 */
const PushViewport = styled(Box)`
  width: inherit;
  overflow-x: clip;
`;

const PushSurface = styled(Box)`
  width: inherit;
  transition: transform ${SCREEN_MS}ms ${SCREEN_EASE};

  &[data-overlay="true"] {
    isolation: isolate;
  }

  &[data-pushed="true"] {
    transform: translateX(${HOME_PUSH_PERCENT}%);
    will-change: transform;
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &[data-pushed="true"] {
      transition: none;
      transform: none;
      will-change: auto;
    }
  }
`;

/**
 * Home is the only surface that pushes. Shop, the collab FTUX and the brand
 * campaign screen open the same journey, but the spec only choreographs the
 * pair against Home, so elsewhere the journey simply arrives over a still page.
 */
const SetupJourneyPushSurface = ({ children }) => {
  const { pathname } = useLocation();
  const isOverlayMounted = useIsSetupJourneyOverlayMounted();
  const isOverlayOpen = useIsSetupJourneyOverlayOpen();

  // Driven by the same boolean as the journey's own travel, so the two surfaces
  // turn on the same commit instead of racing two animation clocks.
  const isPushed = isOverlayOpen && pathname === PAGE_URLS.HOME;

  return (
    <PushViewport data-cy="setup-journey-push-viewport">
      <PushSurface
        data-cy="setup-journey-push-surface"
        data-overlay={String(isOverlayMounted)}
        data-pushed={String(isPushed)}
      >
        {children}
      </PushSurface>
    </PushViewport>
  );
};

export default SetupJourneyPushSurface;
